import React from "react";
import { LegalDocument } from "@/components/LegalDocument";
import { installerBranding } from "@/branding/Branding";
import { appConfig } from "@/config/app";

const sections = [
    { type: "p", text:
        `Welcome to ${appConfig.name}. These Terms of Use ("Terms") set out the rules for using ` +
        `the ${appConfig.name} web portal provided to you by ${installerBranding.name} in ` +
        `connection with your renewable-energy installation. By signing in you accept these Terms.`,
    },

    { type: "h", text: "1. Who can use the app" },
    { type: "list", items: [
        `You must be at least 18 years old to use ${appConfig.name}.`,
        "Homeowner accounts are for the person named on the installation contract.",
        "Engineer accounts are for authorised employees or subcontractors of the installer.",
        "You are responsible for keeping your sign-in credentials confidential.",
    ]},

    { type: "h", text: "2. Your account" },
    { type: "p", text:
        "You must provide accurate information when you sign in. You are responsible for all " +
        "activity that takes place under your account. If you believe your account has been " +
        "compromised, contact us immediately using the details at the bottom of this page." },

    { type: "h", text: "3. Acceptable use" },
    { type: "p", text: "You agree that you will not, and will not attempt to:" },
    { type: "list", items: [
        "Use the app for any unlawful, harmful or fraudulent purpose.",
        "Upload files containing malware, viruses or other harmful code.",
        "Impersonate any person or misrepresent your relationship with any person.",
        "Interfere with, disrupt or reverse-engineer any part of the service.",
        "Access the accounts, projects or messages of other users without authority.",
        "Copy, resell or otherwise commercially exploit the app or its content.",
    ]},

    { type: "h", text: "4. Documents and content" },
    { type: "p", text:
        "You retain ownership of documents you upload. You grant us a limited licence to store, " +
        "display and transmit those documents so that we can provide the service. Documents that " +
        "we generate on your behalf (quotes, certificates, handover packs) remain subject to the " +
        "usual professional and regulatory retention requirements." },

    { type: "h", text: "5. Messages and communications" },
    { type: "p", text:
        "In-app messages are business communications relating to your installation. Please keep " +
        "them relevant and courteous. We reserve the right to remove content that breaches these " +
        "Terms or is otherwise inappropriate." },

    { type: "h", text: "6. Availability and updates" },
    { type: "p", text:
        "We aim to keep the app available at all times but may need to take it offline for " +
        "maintenance, updates or reasons outside our control. We may add, change or remove " +
        "features at any time. We will let you know about material changes through the app." },

    { type: "h", text: "7. Deleting your account" },
    { type: "p", text:
        "You can delete your account at any time from the Profile screen. Deletion is immediate " +
        "and, for homeowners, will also remove your project, documents, appointments and messages " +
        "from the app. Please export anything you want to keep before deleting your account." },

    { type: "h", text: "8. Limitation of liability" },
    { type: "p", text:
        "The app is a tool to help you track your installation — it does not replace the terms of " +
        `your installation contract with ${installerBranding.name}. To the extent permitted by law, ` +
        "we exclude liability for any indirect or consequential loss arising from your use of the " +
        "app. Nothing in these Terms limits our liability for death, personal injury caused by our " +
        "negligence, or fraud." },

    { type: "h", text: "9. Intellectual property" },
    { type: "p", text:
        `${appConfig.name} is proprietary software owned by ${appConfig.copyrightOwner}. All ` +
        `intellectual property rights in the app, its branding and design are reserved. See the ` +
        "Licences page for the list of open-source components used to build the app." },

    { type: "h", text: "10. Governing law" },
    { type: "p", text:
        "These Terms are governed by the laws of England and Wales. Any dispute arising in " +
        "connection with the app will be subject to the exclusive jurisdiction of the courts of " +
        "England and Wales." },

    { type: "h", text: "11. Changes to these Terms" },
    { type: "p", text:
        "We may update these Terms from time to time. The effective date at the top of the page " +
        "indicates when they were last revised. Continuing to use the app after a change confirms " +
        "your acceptance of the updated Terms." },
];

export default function TermsOfUse() {
    return (
        <LegalDocument
            title="Terms of Use"
            subtitle={`The rules for using ${appConfig.name} with ${installerBranding.name}.`}
            effectiveDate={appConfig.releaseDate}
            sections={sections}
            testid="legal-terms-screen"
        />
    );
}
